import type { AudioPlaybackRepository } from '@/domain/Repositories';
import type { PlaybackSnapshot } from '@/domain/Models';
import { getAudioObjectUrl } from './AudioStore';

type Listener = (snapshot: PlaybackSnapshot) => void;

export class AudioPlaybackRepositoryImpl implements AudioPlaybackRepository {
  private audio: HTMLAudioElement | null = null;
  private objectUrl: string | null = null;
  private playbackSequence = 0;
  private listeners = new Set<Listener>();
  private current: PlaybackSnapshot = {
    playbackId: 0,
    assetId: null,
    state: 'IDLE',
    positionMs: 0,
    durationMs: 0,
    speed: 1,
    errorCode: null,
  };

  snapshot(): PlaybackSnapshot {
    return { ...this.current };
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    const snap = this.snapshot();
    for (const l of this.listeners) l(snap);
  }

  private ensureAudio(): HTMLAudioElement {
    if (!this.audio) {
      this.audio = new Audio();
      this.audio.preload = 'auto';
      this.audio.addEventListener('timeupdate', () => {
        if (this.current.state === 'IDLE') return;
        this.current.positionMs = Math.floor((this.audio?.currentTime ?? 0) * 1000);
        this.emit();
      });
      this.audio.addEventListener('loadedmetadata', () => {
        if (this.current.state === 'IDLE') return;
        this.current.durationMs = Math.floor((this.audio?.duration ?? 0) * 1000);
        this.emit();
      });
      this.audio.addEventListener('ended', () => {
        if (this.current.state === 'IDLE' || this.current.state === 'COMPLETED') return;
        this.current.positionMs = Math.floor((this.audio?.duration ?? 0) * 1000);
        this.current.state = 'COMPLETED';
        this.emit();
      });
      this.audio.addEventListener('error', () => {
        if (this.current.state === 'IDLE' || this.current.state === 'COMPLETED') return;
        this.current.state = 'FAILED';
        this.current.errorCode = 'AUDIO_PLAYBACK_FAILED';
        this.emit();
      });
    }
    return this.audio;
  }

  async play(assetId: string, speed: number): Promise<void> {
    const playbackId = ++this.playbackSequence;
    this.current = {
      playbackId,
      assetId,
      state: 'BUFFERING',
      positionMs: 0,
      durationMs: 0,
      speed,
      errorCode: null,
    };
    this.emit();

    const url = await getAudioObjectUrl(assetId);
    if (playbackId !== this.playbackSequence) {
      if (url) URL.revokeObjectURL(url);
      return;
    }
    if (!url) {
      this.current = {
        playbackId,
        assetId,
        state: 'FAILED',
        positionMs: 0,
        durationMs: 0,
        speed,
        errorCode: 'AUDIO_NOT_FOUND',
      };
      this.emit();
      return;
    }
    const audio = this.ensureAudio();
    audio.pause();
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = url;
    audio.src = url;
    audio.playbackRate = speed;
    try {
      await audio.play();
      if (playbackId !== this.playbackSequence) return;
      this.current.state = 'PLAYING';
      this.emit();
    } catch {
      if (playbackId !== this.playbackSequence) return;
      this.current.state = 'FAILED';
      this.current.errorCode = 'AUDIO_PLAY_BLOCKED';
      this.emit();
    }
  }

  async pause(): Promise<void> {
    this.audio?.pause();
    if (this.current.state === 'PLAYING' || this.current.state === 'BUFFERING') {
      this.current.state = 'PAUSED';
      this.emit();
    }
  }

  async resume(): Promise<void> {
    if (!this.audio) return;
    if (this.current.state !== 'PAUSED' && this.current.state !== 'BUFFERING') return;
    const playbackId = this.current.playbackId;
    try {
      await this.audio.play();
      if (playbackId !== this.playbackSequence) return;
      this.current.state = 'PLAYING';
      this.current.speed = this.audio.playbackRate;
      this.emit();
    } catch {
      if (playbackId !== this.playbackSequence) return;
      this.current.state = 'FAILED';
      this.current.errorCode = 'AUDIO_PLAY_BLOCKED';
      this.emit();
    }
  }

  async seekTo(positionMs: number): Promise<void> {
    if (!this.audio || this.current.state === 'IDLE') return;
    const maxMs = this.current.durationMs > 0 ? this.current.durationMs : Number.POSITIVE_INFINITY;
    const nextMs = Math.min(maxMs, Math.max(0, Math.floor(positionMs)));
    this.audio.currentTime = nextMs / 1000;
    this.current.positionMs = nextMs;
    this.emit();
  }

  async stop(): Promise<void> {
    const playbackId = ++this.playbackSequence;
    if (this.audio) {
      this.audio.pause();
      this.audio.removeAttribute('src');
      this.audio.load();
    }
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
    this.current = {
      playbackId,
      assetId: null,
      state: 'IDLE',
      positionMs: 0,
      durationMs: 0,
      speed: 1,
      errorCode: null,
    };
    this.emit();
  }
}
